import { useState } from 'react'
import {
  Box, Button, TextField, Alert, CircularProgress, Stack, Typography,
  ToggleButton, ToggleButtonGroup, InputAdornment,
} from '@mui/material'
import { requestManualAction, fetchSuggestions, executeStepAction } from './api.js'
// The order note's default text (2026-10-29), shared with the main panel's
// refund step so both start with the same words.
import { ebayRefundNoteDefault as refundNoteDefault } from './suggestionActionInputs.js'
import ExpandableTextField from './ExpandableTextField.jsx'
import DraggableModal from './DraggableModal.jsx'
import { EbayCaseLink, LinkedCaseText } from './EbayCaseText.jsx'
import { EbayRefundRouteNote } from './EbayRefundRouteNote.jsx'

// Exported so SuggestionPanel.jsx's own numbered-Proposed-Step rendering of
// issue_ebay_return_refund (actions.js's automatedReturnNoticeRefundOnlyActions())
// can default to the SAME comment text this modal already uses, rather than
// a second, drifting copy of the same string.
export const FULL_REFUND_COMMENT = 'A full refund has been issued, the part does not need to be returned.'
export const PARTIAL_REFUND_COMMENT = 'A refund has been issued, the part does not need to be returned.'

function isFormSubmittable(refundType, comment, amount, orderTotal) {
  if (!refundType || !comment?.trim()) return false
  if (refundType === 'full') return true
  const value = Number(amount)
  return value > 0 && value <= orderTotal
}

// Shared "eBay Seller Central" refund modal — parameterized by which backend
// action type to submit to, since "Create Refund" (no eBay Gen exists yet)
// and "Issue Refund" (an eBay Gen already exists) share almost all of their
// UI: the Full/Partial toggle, Refund Comment, Refund Amount ($-prefixed,
// clamped to orderTotal), Process button gating, and success/failure
// display. Self-contained the same way ManualActionRow is: its own trigger
// Button + its own DraggableModal, one instance per EBAY_SELLER_CENTRAL_BUTTONS
// entry in ManualActionsSidebar.jsx. offsetIndex/onOpenChange come from that
// sidebar's shared multi-instance stagger registry.
//
// requiresReturnId: true for "Issue Refund" — shows a read-only "eBay
// Return ID" field (or a blocking notice when none was found) above
// instructionalText; false for "Create Refund", which has no such field.
export default function RefundModal({
  conversationId, onActionCompleted,
  actionType, label, instructionalText, requiresReturnId = false,
  offsetIndex = 0, onOpenChange,
}) {
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [loaded, setLoaded] = useState(null) // { suggestionId, actionIndex, noteActionIndex, action }
  const [refundType, setRefundType] = useState(null) // 'full' | 'partial' | null
  const [comment, setComment] = useState('')
  const [amount, setAmount] = useState('')
  const [processState, setProcessState] = useState(null) // { status: 'processing'|'success'|'failed', result, error }
  // The order note (2026-10-29): shown with the refund, sent with it
  // (editedNote), added to the order right after the eBay refund succeeds.
  // It follows the refund type, amount and comment until the person edits it.
  const [noteText, setNoteText] = useState('')
  const [noteEdited, setNoteEdited] = useState(false)

  function load() {
    setLoading(true)
    setLoadError(null)
    setLoaded(null)
    setRefundType(null)
    setComment('')
    setAmount('')
    setProcessState(null)
    setNoteText('')
    setNoteEdited(false)
    requestManualAction(conversationId, actionType)
      .then(async (res) => {
        const list = await fetchSuggestions(conversationId)
        const suggestion = list.find((s) => s.id === res.suggestionId)
        const action = suggestion?.proposedActions?.[res.actionIndex]
        if (!suggestion || !action) throw new Error('Could not load the resulting action')
        // The refund step carries the order note (2026-10-29): no separate
        // note step (the server may offer the eBay return's or the INR
        // inquiry's refund instead, 2026-10-27; each the same).
        setLoaded({ suggestionId: res.suggestionId, actionIndex: res.actionIndex, action })
      })
      .catch((e) => setLoadError(e.message))
      .finally(() => setLoading(false))
  }

  function openModal() {
    setExpanded(true)
    onOpenChange?.(true)
    load()
  }

  // Closing (via the trigger button again, the modal's own close button, or
  // Escape) resets every bit of state, same as before — reopening always
  // starts a fresh grounding, never reusing stale state from a prior open.
  function closeModal() {
    setExpanded(false)
    onOpenChange?.(false)
    setLoaded(null)
    setLoadError(null)
    setRefundType(null)
    setComment('')
    setAmount('')
    setProcessState(null)
    setNoteText('')
    setNoteEdited(false)
  }

  function handleToggle() {
    if (expanded) closeModal(); else openModal()
  }

  function handleRefundTypeChange(value) {
    setRefundType(value)
    setComment(value === 'full' ? FULL_REFUND_COMMENT : value === 'partial' ? PARTIAL_REFUND_COMMENT : '')
    if (value !== 'partial') setAmount('')
  }

  const orderTotal = loaded?.action?.params?.orderTotal ?? 0
  const returnId = loaded?.action?.params?.returnId ?? null
  // What the server offered (2026-10-27, step-builders.js's
  // caseAwareEbayRefundSteps()): with an open eBay case on the order, the
  // refund goes through the eBay return (issue_ebay_return_refund) or the
  // INR inquiry (inr_issue_refund, always the full refund, the comment is
  // what the buyer reads); an open eBay case with no CXHub step is advice
  // only (the 34916 hint), no Process.
  const loadedType = loaded?.action?.type ?? null
  const viaInquiry = loadedType === 'inr_issue_refund'
  const adviceOnly = loadedType === 'advise_manual_review'
  const caseRefundNote = loaded?.action?.params?.caseRefundNote ?? null
  // The eBay refund route (2026-10-29): which way the refund goes, its id
  // linked; an older step keeps its caseRefundNote.
  const refundRoute = loaded?.action?.params?.ebayRefundRoute ?? null
  const returnIdMissing = (loadedType ? loadedType === 'issue_ebay_return_refund' : requiresReturnId) && !returnId
  const noteShown = noteEdited ? noteText : refundNoteDefault({ viaInquiry, ebayCase: loaded?.action?.params?.ebayCase ?? '', returnId, onReturn: loadedType === 'issue_ebay_return_refund', refundType: viaInquiry ? 'full' : refundType, amount, orderTotal, comment })

  async function handleProcess() {
    if (!loaded) return
    setProcessState({ status: 'processing', result: null, error: null })
    try {
      const base = viaInquiry ? { refundType: 'full', comment, editedMessage: comment } : { refundType, comment, ...(refundType === 'partial' ? { amount } : {}) }
      const value = { ...base, editedNote: noteShown }
      const res = await executeStepAction(loaded.suggestionId, loaded.actionIndex, { [loaded.actionIndex]: value }, { source: 'manual_panel' })
      if (res.success) {
        setProcessState({ status: 'success', result: res.result ?? null, error: null })
        // Draft update: the deliberate exception to manual actions' usual
        // skipDraft: true — the CSR explicitly asked to issue a refund, so
        // the draft SHOULD reflect the resolved reply text.
        onActionCompleted?.(actionType, res.result?.replyText)
      } else {
        setProcessState({ status: 'failed', result: null, error: res.error ?? 'Action failed' })
      }
    } catch (e) {
      setProcessState({ status: 'failed', result: null, error: e.message })
    }
  }

  const created = processState?.status === 'success'

  return (
    <Box>
      <Button
        fullWidth
        size="small"
        variant="contained"
        color="primary"
        onClick={handleToggle}
        sx={{ justifyContent: 'flex-start' }}
      >
        {label}
      </Button>
      <DraggableModal
        open={expanded}
        onClose={closeModal}
        title={label}
        centered
        offsetIndex={offsetIndex}
        width={480}
        height={480}
        actions={
          loaded && !created && !adviceOnly ? (
            <Button
              variant="contained"
              disabled={processState?.status === 'processing' || returnIdMissing || !isFormSubmittable(refundType, comment, amount, orderTotal)}
              onClick={handleProcess}
              startIcon={processState?.status === 'processing' ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {processState?.status === 'processing' ? 'Processing…' : 'Process'}
            </Button>
          ) : null
        }
      >
        {loading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="body2">Loading…</Typography></Stack>}
        {loadError && <Alert severity="error">{loadError}</Alert>}

        {loaded && adviceOnly && (
          // An open eBay case with no CXHub step (2026-10-27): eBay's 34916
          // hint, no seller refund offered.
          <Stack spacing={1}>
            <Alert severity="warning" data-testid="refund-case-hint">{loaded.action.humanReviewReason ?? loaded.action.rationale ?? loaded.action.label}</Alert>
            {/* The case number links to the case on eBay (2026-10-28). */}
            {refundRoute && <EbayRefundRouteNote route={refundRoute} testId="refund-modal-route" />}
            {loaded.action.params?.caseId && !refundRoute && (
              <Typography variant="body2" data-testid="refund-case-advice-link">
                eBay case: <EbayCaseLink caseId={loaded.action.params.caseId} kind={loaded.action.params.caseKind ?? 'casemanagement'} testId="refund-case-link" />
              </Typography>
            )}
          </Stack>
        )}

        {loaded && !created && !adviceOnly && (
          <Stack spacing={1.5}>
            {refundRoute
              ? <Alert severity="info" data-testid="refund-case-note"><EbayRefundRouteNote route={refundRoute} testId="refund-modal-route" sx={{ mt: 0 }} /></Alert>
              : caseRefundNote && <Alert severity="info" data-testid="refund-case-note">{caseRefundNote}</Alert>}
            {/* A return eBay escalated to a case (2026-10-28): still
                refunded on the return; if refused, refund it on the case
                in Seller Hub. */}
            {loaded.action?.params?.escalationWarning && (
              <Alert severity="warning" data-testid="refund-escalation-warning">
                <LinkedCaseText text={loaded.action.params.escalationWarning} caseId={loaded.action.params.escalatedCaseId} kind="escalated_return" testId="refund-escalation-case-link" />
              </Alert>
            )}
            {loadedType === 'issue_ebay_return_refund' && (
              returnId
                ? <TextField size="small" label="eBay Return ID" disabled value={returnId} />
                : (
                  <Typography variant="body2" fontWeight={700} color="error">
                    eBay Gen has not been created yet, use the Create Refund option.
                  </Typography>
                )
            )}
            <Typography variant="body2" fontWeight={700} color="text.primary">
              {instructionalText}
            </Typography>
            <ToggleButtonGroup
              value={refundType}
              exclusive
              onChange={(_e, v) => { if (v) handleRefundTypeChange(v) }}
              size="small"
            >
              <ToggleButton value="full">Full Refund</ToggleButton>
              <ToggleButton value="partial" disabled={viaInquiry}>Partial Refund</ToggleButton>
            </ToggleButtonGroup>
            {refundType && (
              <ExpandableTextField
                size="small" label="Refund Comment" multiline minRows={2} fullWidth
                value={comment} onChange={(e) => setComment(e.target.value)}
              />
            )}
            {refundType === 'partial' && (
              <TextField
                size="small" label="Refund Amount" type="number" value={amount}
                onChange={(e) => setAmount(String(Math.max(0, Math.min(orderTotal, Number(e.target.value) || 0))))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            )}
            {refundType && (
              // The order note, in the refund step (2026-10-29): added to
              // the order right after the refund succeeds.
              <ExpandableTextField
                size="small" label="Order note (editable)" multiline minRows={3} fullWidth
                value={noteShown} onChange={(e) => { setNoteEdited(true); setNoteText(e.target.value) }}
              />
            )}
            {processState?.status === 'failed' && <Alert severity="error">{processState.error}</Alert>}
          </Stack>
        )}

        {created && (
          <Stack spacing={1.5}>
            <Alert severity="success">Refund issued.</Alert>
            {/* The note went with the refund (2026-10-29): added, or why not. */}
            {processState.result?.orderNote?.written && <Alert severity="success">Note added.</Alert>}
            {processState.result?.orderNote && !processState.result.orderNote.written && (
              <Alert severity="warning" data-testid="refund-note-not-added">{processState.result.orderNote.error}</Alert>
            )}
          </Stack>
        )}
      </DraggableModal>
    </Box>
  )
}
